import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { useTimezone } from '../../../lib/TimezoneProvider';

import SimpleTable from '../../SimpleTable';
import UploadDialog from '../../UploadDialog';
import LeadDetailsDrawer from '../../LeadDetailsDrawer/LeadDetailsDrawer';
import { ClientRecord, DegreeProgramRecord, getRemoteId, LeadRecord, VendorRecord } from '../../../lib/DataModel'
import { download, failOnHttpError, getStorage, groupBy, setStorage, stripHTML } from '../../../lib/utils'
import { buildQueryString, loadTableData } from '../../table-utils';
import useClientMetadata from '../../../hooks/useClientMetadata'

import DeleteImage from 'delete-icon.png'
import DetailsImage from 'details-icon.png'

import UploadIcon from '@mui/icons-material/FileUploadOutlined';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';

import Box from "@mui/material/Box";
import { useNotifications } from "lib/NotificationsProvider";
import { useFeatures } from "providers/FeaturesProvider";
import PlainDate from "lib/PlainDate";
import moment from 'moment-timezone'
import { useOrbit } from "providers/OrbitProvider";
import { useCurrentUser } from "lib/CurrentUserProvider";
import { getAllAccessibleClientIds, getAllAccessibleVendorIds } from "components/PerformanceTable/PerformanceTable";
import ExportStatusDialog from "./ExportStatusDialog";
import AlertBadge from "components/UI/AlertBadge/AlertBadge"
import Tooltip from "@mui/material/Tooltip"
import colors from "lib/colors"
import DownloadingIcon from '@mui/icons-material/Downloading'
import AiChatContainer from "./AiChatContainer";
import { TableAction } from "components/SimpleTable/TableTitle";
import { getIn } from "components/Formik/forms";
import { someVendorPermitLandingPages } from "lib/auth-helpers";
import { DataManager } from "components/SimpleTable/DataManager";
import MainContainer from "components/layout/MainContainer/MainContainer";
import { Column } from "components/SimpleTable/types";
import ResetFiltersIcon from "components/UI/icons/ResetFiltersIcon";


const buildColumns = ({ clientCampaignCustomAttributes, timezone }) => [
  { title: 'VEGA ID', field: 'publicId' },
  { title: 'Date', field: 'createdAt', type: 'datetime', render: m => <Box sx={{ whiteSpace: 'nowrap' }}>{moment.tz(m, timezone).format('M/D/YY, h:mm A')}</Box> },
  { title: 'Client', field: 'clientId', lookup: {}, subfield: 'degreeProgramId' },
  { title: 'Vendor', field: 'vendorId', lookup: {} },
  { title: 'Degree Program', field: 'degreeProgramId', lookup: {} },
  { title: 'External ID', field: 'externalId' },
  { title: 'SubID', field: 'subid' },
  { title: 'Source', field: 'source', render: source => source || '(uncategorized)' },
  ...clientCampaignCustomAttributes.map(attribute => ({ title: attribute, field: `clientCampaignCustomAttributes:${attribute}`, render: (_value, row) => getIn(row, `clientCampaignCustomAttributes.${attribute}`) })),
  { title: 'First name', field: 'firstName' },
  { title: 'Last name', field: 'lastName' },
  { title: 'Email', field: 'email' },
  { title: 'Phone Number', field: 'phone' },
  { title: 'Street Address', field: 'streetAddress' },
  { title: 'City', field: 'city' },
  { title: 'State', field: 'state' },
  { title: 'ZIP Code', field: 'zipCode' },
  { title: 'Status', field: 'status', lookup: {}},
  { title: 'Status Code', field: 'statusCode'},
  { title: 'Disposition', field: 'disposition', lookup: {}},
  { title: 'Status changed at', field: 'statusChangedAt', type: 'datetime', render: m => <Box sx={{ whiteSpace: 'nowrap' }}>{moment.tz(m, timezone).format('M/D/YY, h:mm A')}</Box> },
  { title: 'Billable', field: 'billable', render: (billable, _row) => {
    return (
    <Chip
      label={billable}
      color={billable === 'Yes' ? 'success' : 'error'}
    />
  )}, lookup: {
    true: 'Yes',
    false: 'No',
  }, filterExclusive: true},
  { title: 'Lead amount', field: 'leadAmountCents', render: leadAmountCents => {
    return leadAmountCents && `$${Math.floor(leadAmountCents/100)}.${(leadAmountCents%100).toString().padStart(2, '0')}`
  }},
  { title: 'IP Address', field: 'remoteIp' },
  { title: 'User Agent', field: 'userAgent' },
  { title: 'Secondary TCPA', field: 'secondaryTcpa', lookup: {
    true: 'Yes',
    false: 'No',
  }},
  { title: 'Rejection reason', field: 'rejectionReason' },
  { title: 'Filtered reason', field: 'filteredReason' },
  { title: 'Raw request parameters', field: 'rawParams' },
  { title: 'Tracking parameters', field: 'trackingParams' },
  { title: 'Test', field: 'test', type: 'checkbox', render: test => (
    <Chip
      label={test ? 'Yes' : 'No'}
      color={test ? 'success' : 'error'}
    />
  )},
  { title: 'Landing Page ID', field: 'landingPageId' }
]

// FIXME: duplicated in PerformanceTable
const mapToName = function(records) {
  return records.reduce((hash, record) => { hash[getRemoteId(record) ?? ''] = stripHTML(record.attributes.internalName || record.attributes.name || record.attributes.description); return hash; }, {});
};

function populateColumns({ permitLandingPages, userHasMultipleVendors, userHasMultipleClients, timezone, columnProps, clientCampaignCustomAttributes }) {
  const visibleColumns = getStorage('leads.visibleColumns') || ['createdAt', 'clientId', 'degreeProgramId', 'subid', 'source', 'firstName', 'lastName', 'email', 'status', 'billable', 'rejectionReason', 'filteredReason']

  return buildColumns({ clientCampaignCustomAttributes, timezone })
    .filter(column => permitLandingPages || !['landingPageId'].includes(column.field))
    .filter(column => userHasMultipleClients || !['clientId'].includes(column.field))
    .filter(column => userHasMultipleVendors || column.field !== 'vendorId')
    .map(column => {
      if (column.field === 'createdAt') {
        return { ...column, defaultFilter: { operator: 'between', value: [PlainDate.from(moment.tz(timezone).format('YYYY-MM-DD')), PlainDate.from(moment.tz(timezone).format('YYYY-MM-DD'))] }}
      } else
        return column
    })
    .map(column => ({
      ...column,
      ...columnProps[column.field],
      visible: visibleColumns.includes(column.field),
    }))
    .sort((a,b) => a.visible ? (b.visible ? (visibleColumns.indexOf(a.field) - visibleColumns.indexOf(b.field)) : -1) : (b.visible ? 1 : 0))
}

export interface ExportStatus {
  status: 'running' | 'queued' | 'discarded' | 'succeeded'
  statusURL?: string
  error?:  string
  url?: string
}

type LeadDataRow = LeadRecord['attributes']

export default function LeadTable() {
  const { currentOrganization, accessibleClients, accessibleVendorContracts, accessibleVendors, accessibleClientContracts, getClientRole } = useCurrentUser()
  const { remote, getRemoteId } = useOrbit()
  const { addNotification } = useNotifications()
  const dataManagerRef = useRef<DataManager<LeadDataRow>>(null)
  const permitAiAnalysis = !!getIn(currentOrganization, 'attributes.permitAiAnalysis')
  const allAccessibleClientIds = useMemo(() => getAllAccessibleClientIds(getRemoteId, accessibleClients, accessibleVendorContracts), [accessibleClients, accessibleVendorContracts, getRemoteId])
  const userHasMultipleClients = allAccessibleClientIds.length > 1
  const pickClientIds = useCallback(() => {
    if(userHasMultipleClients) {
      const clientIdFilter = dataManagerRef.current?.filters?.clientId
      const clientIds = clientIdFilter?.value || []
      return clientIds
    }
    else {
      return allAccessibleClientIds
    }
  }, [allAccessibleClientIds, userHasMultipleClients])
  const [clientIds, setClientIds] = useState(pickClientIds)
  const hasPermitEditRoleForSomeClient = useMemo(() => allAccessibleClientIds.some(clientId => !!getIn(getClientRole(clientId), 'attributes.permitEdit')), [allAccessibleClientIds, getClientRole])
  const permitDelete = hasPermitEditRoleForSomeClient
  const permitUpload = hasPermitEditRoleForSomeClient
  const allAccessibleVendorIds = getAllAccessibleVendorIds(getRemoteId, accessibleVendors, accessibleClientContracts)
  const userHasMultipleVendors = allAccessibleVendorIds.length > 1
  const permitLandingPages = someVendorPermitLandingPages({accessibleVendors})

  const [clientIdToName, setClientIdToName] = useState({});
  const [vendorIdToName, setVendorIdToName] = useState({});
  const [degreeProgramIdToName, setDegreeProgramIdToName] = useState({});
  const [clientIdToDegreePrograms, setClientIdToDegreePrograms] = useState({});
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showLeadDetails, setShowLeadDetails] = useState<LeadRecord | null>();
  const { timezone } = useTimezone()
  const { isFeatureFlagEnabled } = useFeatures()
  const [showExportModal, setShowExportModal] = useState(false)

  useEffect(() => {
    const promises: [Promise<ClientRecord[]>, Promise<VendorRecord[]>, Promise<DegreeProgramRecord[]>] = [
      remote.query(q => q.findRecords('client')),
      remote.query(q => q.findRecords('vendor')),
      remote.query<DegreeProgramRecord[]>(q => q.findRecords('degreeProgram'), {
        sources: {
          remote: {
            settings: {
              params: {
                fields: {
                  'degree-program': ['name', 'client'].join(',')
                }
              }
            }
          }
        }
      }),
    ]
    Promise.all(promises).then(([clients, vendors, degreePrograms]) => {
      setClientIdToName(mapToName(clients));
      setVendorIdToName(mapToName(vendors));
      setDegreeProgramIdToName(mapToName(degreePrograms));
      setClientIdToDegreePrograms(groupBy(degreePrograms, 'relationships.client.data.id', { transform: (degreeProgram: DegreeProgramRecord) => degreeProgram.id }))
      setLoading(false);
    }).catch(error => {
      console.log(error);
      addNotification({variant: 'alert', message: 'Error loading leads'})
      setLoading(false);
    })
  }, [addNotification, remote]);

  const { statuses, dispositions, clientCampaignCustomAttributes } = useClientMetadata(clientIds)

  const columns: Column<LeadDataRow>[] = useMemo(
    () => populateColumns({
      permitLandingPages,
      userHasMultipleVendors,
      userHasMultipleClients,
      timezone,
      columnProps: {
        clientId: { lookup: clientIdToName, subfieldMap: clientIdToDegreePrograms },
        vendorId: { lookup: vendorIdToName },
        degreeProgramId: { lookup: degreeProgramIdToName },
        status: { lookup: statuses },
        disposition: { lookup: dispositions },
      },
      clientCampaignCustomAttributes,
    }),
    [clientIdToDegreePrograms, clientIdToName, degreeProgramIdToName, dispositions, permitLandingPages, statuses, timezone, userHasMultipleClients, userHasMultipleVendors, vendorIdToName, clientCampaignCustomAttributes]
  )

  const dataFn = useMemo(() => loadTableData(remote, q => q.findRecords('lead'), addNotification, timezone), [remote, addNotification, timezone])

  const handleChange = useCallback(() => {
    setClientIds(pickClientIds())
  }, [pickClientIds])

  const onChangeColumns = useCallback(columns => {
    setStorage('leads.visibleColumns', columns.filter(column => column.visible).map(column => column.field))
  }, [])

  const refreshTable = useCallback(() => {
    dataManagerRef.current?.reload();
  }, [dataManagerRef])
  const handleDelete = useCallback((_event, rowData) => {
    if(!confirm(`Are you sure you want to delete the lead ${rowData['firstName']} ${rowData['lastName']}?`)) {
      return;
    }

    fetch(remote.requestProcessor.urlBuilder.resourceURL('lead', rowData.id), { method: 'DELETE' })
      .then(failOnHttpError)
      .then(() => {
        addNotification({variant: 'notice', message: 'Deleted lead.'});
        refreshTable();
      })
      .catch(_error => {
        addNotification({variant: 'alert', message: 'Failed to delete lead.'});
      });
  }, [addNotification, refreshTable, remote.requestProcessor.urlBuilder])

  const [exportStatus, setExportStatus] = useState<ExportStatus|null>(null)

  const handleExport = useCallback(_event => {
    if(!dataManagerRef.current) {
      return
    }

    if(exportStatus) {
      setShowExportModal(true)
      return
    }

    const urlBuilder = remote.requestProcessor.urlBuilder
    const baseURL = urlBuilder.resourceURL('lead')
    const dataOptions = dataManagerRef.current.getDataOptions()
    const dataOptionsWithTimezone = {...dataOptions, timezone}
    if(isFeatureFlagEnabled('offline_lead_exports')) {
      setExportStatus({ status: 'running' })
      setShowExportModal(true)
      fetch(`${baseURL}.csv?${buildQueryString(dataOptionsWithTimezone)}`)
        .then(failOnHttpError)
        .then(response => response.json())
        .then(result => {
          setExportStatus({ status: 'queued', statusURL: result.status_url })
        })
        .catch(_error => {
          setExportStatus({ status: 'discarded', error: 'Failed to schedule export job' })
        });
    }
    else {
      download(`${baseURL}.csv?${buildQueryString(dataOptionsWithTimezone)}`, 'leads.csv')
    }
  }, [exportStatus, isFeatureFlagEnabled, remote.requestProcessor.urlBuilder, timezone])

  const handleImport = useCallback(_event => {
    setShowUploadDialog(true);
  }, [setShowUploadDialog])

  const handleShowDetails = useCallback((_event, rowData) => {
    setShowLeadDetails(rowData)
  }, [setShowLeadDetails])

  const handleHideDetails = useCallback((_event) => {
    setShowLeadDetails(null)
  }, [setShowLeadDetails])

  const exportCsvIcon =
    <Box sx={{position: 'relative', height: '28px'}}>
      {!!exportStatus && (exportStatus.status == 'running' || exportStatus.status == 'queued') && (
        <Box sx={{ position: 'absolute', left: '-10px', top: '-10px'}}>
          <Tooltip title='Click for Status Update'>
              <div>
                <DownloadingIcon style={{ color: colors.coastalWaters, display: 'block' }}/>
              </div>
          </Tooltip>
        </Box>
      )}
      {!!exportStatus && exportStatus.status == 'discarded' && (
        <AlertBadge tipTitle='Error' sx={{left: '-10px', top: '-11px'}}/>
      )}
      {!!exportStatus && exportStatus.status == 'succeeded' && (
        <AlertBadge variant='success' tipTitle='Success' sx={{left: '-10px', top: '-11px'}}/>
      )}
      <DownloadIcon />
    </Box>


  if(loading) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
        <CircularProgress/>
      </div>
    )
  } else {
    const rowActions = [{
      icon: DetailsImage,
      tooltip: 'Lead Details',
      onClick: handleShowDetails,
    }]
    const actions: TableAction[] = [
      {
        icon: <ResetFiltersIcon />,
        tooltip: 'Reset Filters',
        onClick: () => dataManagerRef.current?.resetFilters(),
      },
      {
        icon: exportCsvIcon,
        tooltip: exportStatus ? '' : 'Export to CSV',
        onClick: handleExport,
      },
    ]

    if(permitDelete) {
      rowActions.push({
        icon: DeleteImage,
        tooltip: 'Delete Lead',
        onClick: handleDelete,
      })
    }
    if(permitUpload) {
      actions.push({
        icon: <UploadIcon />,
        tooltip: 'Import CSV',
        onClick: handleImport,
      })
    }

    // TODO: the data prop changes every time LeadTable is rendered, which causes
    // the SweetTable to get unnecessarily rerendered.
    return (
      <MainContainer fullWidth>
        {!!showExportModal && (
          <ExportStatusDialog exportStatus={exportStatus} setExportStatus={setExportStatus} setShowExportModal={setShowExportModal} />
        )}
        <SimpleTable
          dataManagerRef={dataManagerRef}
          columns={columns}
          data={dataFn}
          options={{
            filtering: true,
            paginated: true,
            pageSize: 10,
            columnsButton: true,
            sortColumn: 'createdAt',
            sortOrder: -1,
            refreshButton: true,
            refresh: refreshTable,
          }}
          title='Leads'
          onChangeColumns={onChangeColumns}
          onChange={handleChange}
          actions={actions}
          rowActions={rowActions}
          tableTitleChildren={permitAiAnalysis && <AiChatContainer/>}
        />
        {showUploadDialog && <UploadDialog onComplete={refreshTable} onClose={() => setShowUploadDialog(false)}/>}
        <LeadDetailsDrawer columns={columns} lead={showLeadDetails} onClose={handleHideDetails}/>
      </MainContainer>
    );
  }
}
